import { useState } from 'react';
import { pdf, Font, Page, Text, View, Document, StyleSheet } from '@react-pdf/renderer';

import DownloadIcon from '@mui/icons-material/Download';
import { Alert, Button, Tooltip, Snackbar, IconButton, CircularProgress } from '@mui/material';

import dayjs from 'src/utils/dayjs';

import * as Supplier from 'src/api/supplier';
import * as Applications from 'src/api/applications';

import {
  fmt,
  stripHtml,
  fmtCurrency,
  pdfFilename,
  fmtTimestamp,
  triggerPdfDownload,
  buildSupplierPdfData,
  buildProcurerPdfData,
} from './utils';

import type { TenderSubmissionData } from './types';

export { buildSupplierPdfData, buildProcurerPdfData } from './utils';
export type { TenderSubmissionData } from './types';

/*
 * Font: Inter (open-source) is used here instead of the project's 'new-hero' (Adobe Typekit).
 *
 * WHY: @react-pdf/renderer generates PDFs at runtime in the browser and needs real font
 * binary files (.ttf / .otf) bundled with the app. Adobe Typekit delivers 'new-hero' only
 * as a CSS @font-face URL from use.typekit.net — the actual font bytes are never exposed
 * to client-side JavaScript, so they cannot be passed to the PDF engine.
 *
 * TO SWITCH TO new-hero WHEN THE FILES BECOME AVAILABLE:
 *   1. Obtain the TTF/OTF files — in Adobe Fonts Kit Editor click "Download for Desktop"
 *      (requires a plan that allows desktop/self-hosting use), or request the files from
 *      the designer / brand team.
 *   2. Place them in client/public/fonts/
 *        e.g.  new-hero-regular.woff   new-hero-bold.woff
 *   3. Replace the Font.register() call below with:
 *
 *        Font.register({
 *          family: 'new-hero',
 *          fonts: [
 *            { src: '/fonts/new-hero-regular.woff', fontWeight: 400 },
 *            { src: '/fonts/new-hero-bold.woff',    fontWeight: 700 },
 *          ],
 *        });
 *
 *   4. Change FONT_FAMILY = 'Inter' → 'new-hero'.
 *      All fontWeight: 700 references in the StyleSheet already handle bold — no other
 *      style changes are needed.
 *   5. Delete client/public/fonts/Inter-Regular.woff and Inter-Bold.woff once validated.
 */
Font.register({
  family: 'Inter',
  fonts: [
    { src: '/fonts/Inter-Regular.woff', fontWeight: 400 },
    { src: '/fonts/Inter-Bold.woff', fontWeight: 700 },
  ],
});

const FONT_FAMILY = 'Inter';
const BRAND_ACCENT = '#fc6743'; // brand orange — decorative borders/accents only

const styles = StyleSheet.create({
  page: {
    padding: 48,
    paddingBottom: 72,
    fontFamily: FONT_FAMILY,
    fontWeight: 400,
    fontSize: 10,
    color: '#1a1a1a',
    lineHeight: 1.5,
  },
  header: {
    marginBottom: 16,
    paddingBottom: 14,
    borderBottom: `2 solid ${BRAND_ACCENT}`,
  },
  headerTitle: {
    fontSize: 22,
    fontWeight: 700,
    marginBottom: 16,
    color: '#1a1a1a',
  },
  headerMeta: {
    fontSize: 9,
    color: '#666',
    marginTop: 0,
  },
  statusPill: {
    marginTop: 7,
    alignSelf: 'flex-start',
    padding: '3 8',
    paddingTop: '7',
    backgroundColor: '#f0f0f0',
    fontSize: 9,
    fontWeight: 700,
    color: '#444',
  },
  section: {
    marginBottom: 20,
  },
  sectionTitle: {
    fontSize: 12,
    fontWeight: 700,
    marginBottom: 10,
    paddingBottom: 4,
    borderBottom: `1 solid ${BRAND_ACCENT}`,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    color: '#1a1a1a',
    minPresenceAhead: 40,
  },
  row: {
    flexDirection: 'row',
    marginBottom: 5,
  },
  label: {
    width: 170,
    fontWeight: 700,
    color: '#555',
    fontSize: 9,
  },
  value: {
    flex: 1,
    fontSize: 10,
  },
  // Geographical Boundaries
  areaGroupLabel: {
    fontSize: 9,
    fontWeight: 700,
    color: '#555',
    marginBottom: 5,
    marginTop: 4,
  },
  areaPillRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  areaPill: {
    padding: '3 8',
    border: '1 solid #ccc',
    fontSize: 9,
    marginRight: 5,
    marginBottom: 5,
  },
  // Themes
  themeCard: {
    marginBottom: 8,
    padding: 10,
    backgroundColor: '#fafafa',
    border: '1 solid #e8e8e8',
  },
  themeTitle: {
    fontWeight: 700,
    fontSize: 10,
    marginBottom: 3,
  },
  themeDescription: {
    fontSize: 9,
    color: '#444',
    lineHeight: 1.5,
  },
  // Additional Information
  infoCard: {
    marginBottom: 8,
    padding: 10,
    backgroundColor: '#fafafa',
    border: '1 solid #e8e8e8',
  },
  infoCardTitle: {
    fontWeight: 700,
    fontSize: 9,
    marginBottom: 3,
  },
  infoCardValue: {
    fontSize: 10,
  },
  // Activities
  activityCard: {
    marginBottom: 10,
    padding: 10,
    backgroundColor: '#fafafa',
    border: '1 solid #e8e8e8',
  },
  activityHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 4,
  },
  activityTitle: {
    fontWeight: 700,
    fontSize: 10,
    flex: 1,
  },
  activityCode: {
    fontSize: 8,
    color: '#888',
    marginLeft: 8,
  },
  activityUnits: {
    fontSize: 9,
    color: '#444',
    fontWeight: 700,
  },
  additionalQA: {
    marginTop: 6,
    paddingTop: 6,
    borderTop: '1 solid #e8e8e8',
  },
  // Commitment Summary
  summaryBox: {
    marginTop: 14,
    padding: 12,
    backgroundColor: '#fafafa',
    border: '1 solid #e8e8e8',
  },
  summaryTitle: {
    fontWeight: 700,
    fontSize: 10,
    marginBottom: 4,
  },
  summarySubtitle: {
    fontSize: 9,
    color: '#666',
    marginBottom: 10,
  },
  summaryRow: {
    flexDirection: 'row',
  },
  summaryCard: {
    flex: 1,
    padding: '8 10',
    border: '1 solid #e8e8e8',
    backgroundColor: '#fff',
    marginRight: 6,
  },
  summaryCardLast: {
    flex: 1,
    padding: '8 10',
    border: '1 solid #e8e8e8',
    backgroundColor: '#fff',
  },
  summaryCardLabel: {
    fontSize: 8,
    color: '#666',
    marginBottom: 3,
  },
  summaryCardValue: {
    fontSize: 11,
    fontWeight: 700,
    color: '#1a1a1a',
  },
  // Q&A
  qaBlock: {
    marginBottom: 10,
    padding: 10,
    backgroundColor: '#fafafa',
    border: '1 solid #e8e8e8',
  },
  qaQuestion: {
    fontWeight: 700,
    marginBottom: 5,
    fontSize: 10,
  },
  qaAnswer: {
    fontSize: 10,
    lineHeight: 1.6,
  },
  qaEmpty: {
    fontSize: 10,
    color: '#999',
  },
  smallLabel: {
    fontSize: 8,
    color: '#888',
    marginBottom: 2,
  },
  noItems: {
    fontSize: 10,
    color: '#999',
  },
  footer: {
    position: 'absolute',
    bottom: 24,
    left: 48,
    right: 48,
    borderTop: '1 solid #e0e0e0',
    paddingTop: 6,
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  footerText: {
    fontSize: 8,
    color: '#aaa',
  },
});

function TenderSubmissionDocument({ data }: { data: TenderSubmissionData }) {
  const generatedAt = dayjs().format('D MMM YYYY [at] HH:mm');

  const hasPriorityAreas = (data.priorityAreas ?? []).length > 0;
  const hasOtherAreas = (data.otherAreas ?? []).length > 0;
  const hasGeography = hasPriorityAreas || hasOtherAreas;
  const hasThemes = (data.themes ?? []).length > 0;
  const hasAdditionalInfo =
    data.applicationOverview ||
    data.instructionsOverview ||
    data.additionalInformation ||
    data.brokerageSystem ||
    (data.documents ?? []).length > 0;

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header */}
        <View style={styles.header}>
          <Text style={styles.headerTitle}>{data.contractTitle}</Text>
          <Text style={styles.headerMeta}>Tender Submission</Text>
          {data.applicationStatus && (
            <View style={styles.statusPill}>
              <Text>{data.applicationStatus}</Text>
            </View>
          )}
        </View>

        {/* Contract Details */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Contract Details</Text>
          {data.contractType && (
            <View style={styles.row}>
              <Text style={styles.label}>Contract Type</Text>
              <Text style={styles.value}>{data.contractType}</Text>
            </View>
          )}
          <View style={styles.row}>
            <Text style={styles.label}>Contract Period</Text>
            <Text style={styles.value}>
              {fmt(data.contractStartDate)} – {fmt(data.contractEndDate)}
            </Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>Application Window</Text>
            <Text style={styles.value}>
              {fmtTimestamp(data.applicationStartDate)} – {fmtTimestamp(data.applicationEndDate)}
            </Text>
          </View>
        </View>

        {/* Geographical Boundaries */}
        {hasGeography && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Geographical Boundaries</Text>
            {hasPriorityAreas && (
              <View>
                <Text style={styles.areaGroupLabel}>Priority areas</Text>
                <View style={styles.areaPillRow}>
                  {data.priorityAreas!.map((area, i) => (
                    <View key={i} style={styles.areaPill}>
                      <Text>{area}</Text>
                    </View>
                  ))}
                </View>
              </View>
            )}
            {hasOtherAreas && (
              <View>
                <Text style={styles.areaGroupLabel}>Other areas</Text>
                <View style={styles.areaPillRow}>
                  {data.otherAreas!.map((area, i) => (
                    <View key={i} style={styles.areaPill}>
                      <Text>{area}</Text>
                    </View>
                  ))}
                </View>
              </View>
            )}
          </View>
        )}

        {/* Themes & Focus Areas */}
        {hasThemes && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Themes & Focus Areas</Text>
            {data.themes!.map((theme, i) => (
              <View key={i} style={styles.themeCard} wrap={false}>
                <Text style={styles.themeTitle}>{theme.title}</Text>
                {theme.description && (
                  <Text style={styles.themeDescription}>{theme.description}</Text>
                )}
              </View>
            ))}
          </View>
        )}

        {/* Additional Information */}
        {hasAdditionalInfo && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Additional Information</Text>
            {data.applicationOverview && (
              <View style={styles.infoCard} wrap={false}>
                <Text style={styles.infoCardTitle}>Application Overview</Text>
                <Text style={styles.infoCardValue}>{stripHtml(data.applicationOverview)}</Text>
              </View>
            )}
            {data.instructionsOverview && (
              <View style={styles.infoCard} wrap={false}>
                <Text style={styles.infoCardTitle}>Instructions Overview</Text>
                <Text style={styles.infoCardValue}>{stripHtml(data.instructionsOverview)}</Text>
              </View>
            )}
            {data.additionalInformation && (
              <View style={styles.infoCard} wrap={false}>
                <Text style={styles.infoCardTitle}>Additional Information</Text>
                <Text style={styles.infoCardValue}>{stripHtml(data.additionalInformation)}</Text>
              </View>
            )}
            {data.brokerageSystem && (
              <View style={styles.infoCard} wrap={false}>
                <Text style={styles.infoCardTitle}>Brokerage System</Text>
                <Text style={styles.infoCardValue}>{stripHtml(data.brokerageSystem)}</Text>
              </View>
            )}
            {(data.documents ?? []).length > 0 && (
              <View style={styles.infoCard} wrap={false}>
                <Text style={styles.infoCardTitle}>Documents</Text>
                {data.documents!.map((doc, i) => (
                  <Text key={i} style={styles.infoCardValue}>
                    • {doc.title}
                  </Text>
                ))}
              </View>
            )}
          </View>
        )}

        {/* Supplier Information */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Supplier Information</Text>
          {data.supplierOrgName && (
            <View style={styles.row}>
              <Text style={styles.label}>Organisation</Text>
              <Text style={styles.value}>{data.supplierOrgName}</Text>
            </View>
          )}
          {data.supplierName && (
            <View style={styles.row}>
              <Text style={styles.label}>Contact Name</Text>
              <Text style={styles.value}>{data.supplierName}</Text>
            </View>
          )}
          {data.contractLeadName && (
            <View style={styles.row}>
              <Text style={styles.label}>Contract Lead</Text>
              <Text style={styles.value}>
                {data.contractLeadName}
                {data.contractLeadEmail ? `  (${data.contractLeadEmail})` : ''}
              </Text>
            </View>
          )}
          {data.socialValueLeadName && (
            <View style={styles.row}>
              <Text style={styles.label}>Social Value Lead</Text>
              <Text style={styles.value}>
                {data.socialValueLeadName}
                {data.socialValueLeadEmail ? `  (${data.socialValueLeadEmail})` : ''}
              </Text>
            </View>
          )}
        </View>

        {/* Committed Activities */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Committed Activities ({data.activities.length})</Text>
          {data.activities.length === 0 ? (
            <Text style={styles.noItems}>No activities committed.</Text>
          ) : (
            data.activities.map((activity, i) => (
              <View key={i} style={styles.activityCard} wrap={false}>
                <View style={styles.activityHeader}>
                  <Text style={styles.activityTitle}>{activity.title}</Text>
                  {activity.code && <Text style={styles.activityCode}>{activity.code}</Text>}
                </View>
                <Text style={styles.activityUnits}>Units committed: {activity.units ?? '—'}</Text>
                {activity.additionalQA.length > 0 && (
                  <View style={styles.additionalQA}>
                    {activity.additionalQA.map((qa, j) => (
                      <View key={j} wrap={false}>
                        <Text style={styles.smallLabel}>{qa.question}</Text>
                        {qa.answer ? (
                          <Text style={styles.qaAnswer}>{qa.answer}</Text>
                        ) : (
                          <Text style={styles.qaEmpty}>Not answered</Text>
                        )}
                      </View>
                    ))}
                  </View>
                )}
              </View>
            ))
          )}

          {/* Commitment Summary */}
          {data.commitmentSummary && (
            <View style={styles.summaryBox} wrap={false}>
              <Text style={styles.summaryTitle}>Commitment Summary</Text>
              <Text style={styles.summarySubtitle}>
                Total Social Value committed based on the activities selected above.
              </Text>
              <View style={styles.summaryRow}>
                <View style={styles.summaryCard}>
                  <Text style={styles.summaryCardLabel}>Market Value</Text>
                  <Text style={styles.summaryCardValue}>
                    {fmtCurrency(data.commitmentSummary.marketValue)}
                  </Text>
                </View>
                <View style={styles.summaryCard}>
                  <Text style={styles.summaryCardLabel}>Impact Value</Text>
                  <Text style={styles.summaryCardValue}>
                    {fmtCurrency(data.commitmentSummary.impactValue)}
                  </Text>
                </View>
                <View style={styles.summaryCardLast}>
                  <Text style={styles.summaryCardLabel}>Points</Text>
                  <Text style={styles.summaryCardValue}>
                    {data.commitmentSummary.points} points
                  </Text>
                </View>
              </View>
            </View>
          )}
        </View>

        {/* Qualitative Questions */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>
            Qualitative Questions ({data.qualitativeQA.length})
          </Text>
          {data.qualitativeQA.length === 0 ? (
            <Text style={styles.noItems}>No qualitative questions for this contract.</Text>
          ) : (
            data.qualitativeQA.map((qa, i) => (
              <View key={i} style={styles.qaBlock} wrap={false}>
                <Text style={styles.qaQuestion}>
                  {i + 1}. {qa.question}
                </Text>
                {qa.answer ? (
                  <Text style={styles.qaAnswer}>{qa.answer}</Text>
                ) : (
                  <Text style={styles.qaEmpty}>Not answered</Text>
                )}
              </View>
            ))
          )}
        </View>

        {/* Footer */}
        <View style={styles.footer} fixed>
          <Text style={styles.footerText}>Generated on {generatedAt}</Text>
          {data.generatedBy && <Text style={styles.footerText}>{data.generatedBy}</Text>}
        </View>
      </Page>
    </Document>
  );
}

export function SupplierApplicationDownloadButton({ applicationId }: { applicationId: number }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const handleDownload = async () => {
    setLoading(true);
    try {
      const data = await Supplier.getSupplierApplication(applicationId);
      const pdfData = buildSupplierPdfData(data);
      const blob = await pdf(<TenderSubmissionDocument data={pdfData} />).toBlob();
      triggerPdfDownload(blob, pdfFilename(pdfData.contractTitle));
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <IconButton size="small" onClick={handleDownload} disabled={loading} sx={{ mr: 0.5 }}>
        {loading ? <CircularProgress size={16} /> : <DownloadIcon fontSize="small" />}
      </IconButton>
      <Snackbar
        open={error}
        autoHideDuration={4000}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        onClose={() => setError(false)}
      >
        <Alert severity="error" variant="filled" onClose={() => setError(false)}>
          Failed to generate PDF. Please try again.
        </Alert>
      </Snackbar>
    </>
  );
}

export function ProcurerApplicationDownloadButton({
  applicationId,
  windowClosed,
  disabled,
}: {
  applicationId: number;
  windowClosed: boolean;
  disabled?: boolean;
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const handleDownload = async () => {
    setLoading(true);
    try {
      const data = await Applications.getApplication(applicationId);
      const pdfData = buildProcurerPdfData(data);
      const blob = await pdf(<TenderSubmissionDocument data={pdfData} />).toBlob();
      triggerPdfDownload(blob, pdfFilename(pdfData.contractTitle));
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Tooltip
        title={
          disabled
            ? 'Unavailable while changes are in progress'
            : windowClosed
              ? 'Download submission PDF'
              : 'Available once the submission window closes'
        }
        placement="left"
      >
        <span>
          <IconButton
            size="small"
            onClick={handleDownload}
            disabled={disabled || !windowClosed || loading}
          >
            {loading ? <CircularProgress size={16} /> : <DownloadIcon fontSize="small" />}
          </IconButton>
        </span>
      </Tooltip>
      <Snackbar
        open={error}
        autoHideDuration={4000}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        onClose={() => setError(false)}
      >
        <Alert severity="error" variant="filled" onClose={() => setError(false)}>
          Failed to generate PDF. Please try again.
        </Alert>
      </Snackbar>
    </>
  );
}

interface DownloadTenderPDFButtonProps {
  data: TenderSubmissionData;
  disabled?: boolean;
}

export function DownloadTenderPDFButton({ data, disabled }: DownloadTenderPDFButtonProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const handleDownload = async () => {
    setLoading(true);
    try {
      const blob = await pdf(<TenderSubmissionDocument data={data} />).toBlob();
      triggerPdfDownload(blob, pdfFilename(data.contractTitle));
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button
        variant="outlined"
        size="medium"
        onClick={handleDownload}
        disabled={disabled || loading}
      >
        {loading ? 'Generating PDF…' : 'Download PDF'}
      </Button>
      <Snackbar
        open={error}
        autoHideDuration={4000}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        onClose={() => setError(false)}
      >
        <Alert severity="error" variant="filled" onClose={() => setError(false)}>
          Failed to generate PDF. Please try again.
        </Alert>
      </Snackbar>
    </>
  );
}
